/**
 * The decisions behind the Intro video tab, as pure functions
 * (spec 2026-10-23 §5). The React components only draw what these return, so
 * every rule below is unit-tested without a DOM
 * (tests/unit/admin/app-experience-intro-state.test.ts).
 *
 * Two kinds of change, kept apart on purpose:
 *   - CONTENT (videos, descriptions, audience) is edited in a DRAFT and goes
 *     live with Publish.
 *   - ON/OFF is the switch. It publishes the LIVE version with `enabled`
 *     flipped, never the draft — turning the intro off must never also ship
 *     a half-finished edit. Publish, in turn, keeps the live on/off state.
 */
import {
  DESCRIPTION_MAX_CHARS,
  validateIntroVideoConfig,
  type ExperienceLocale,
  type IntroVideoConfig,
  type VideoAsset,
} from './contract';
import { formatDuration, shapeLabel } from './video-client-checks';

/** A converted video as the server returns it: a VideoAsset with no description yet. */
export type FinalizedMedia = Omit<VideoAsset, 'description'>;

export const LOCALES: readonly ExperienceLocale[] = ['en', 'es'];
export const LANGUAGE_NAME: Record<ExperienceLocale, string> = { en: 'English', es: 'Spanish' };

export interface IntroDraft {
  /** The audience field as typed; checked by `parseAudience`. */
  audiencePct: string;
  videos: Record<ExperienceLocale, FinalizedMedia | null>;
  /** Kept apart from the video so a replaced video keeps the text the admin wrote. */
  descriptions: Record<ExperienceLocale, string>;
}

export type IntroStatus = 'never' | 'live' | 'off';

const READ_ONLY = 'You can only view this page.';

/** A stored version, read through the shared validator. Never throws; null = cannot read it. */
export function introConfigOf(raw: unknown): IntroVideoConfig | null {
  try {
    const r = validateIntroVideoConfig(raw, null);
    return r.ok ? r.value : null;
  } catch {
    return null;
  }
}

export function statusOf(live: IntroVideoConfig | null): IntroStatus {
  if (!live) return 'never';
  return live.enabled ? 'live' : 'off';
}

function mediaOf(v: VideoAsset | null): FinalizedMedia | null {
  if (!v) return null;
  const { description: _description, ...media } = v;
  return media;
}

export function draftFrom(live: IntroVideoConfig | null): IntroDraft {
  return {
    audiencePct: String(live?.audience_pct ?? 100),
    videos: { en: mediaOf(live?.videos.en ?? null), es: mediaOf(live?.videos.es ?? null) },
    descriptions: { en: live?.videos.en?.description ?? '', es: live?.videos.es?.description ?? '' },
  };
}

/** A whole number from 0 to 100, or null. "50.5", "1e2" and "" are not. */
export function parseAudience(text: string): number | null {
  const t = text.trim();
  if (!/^\d{1,3}$/.test(t)) return null;
  const n = Number(t);
  return n <= 100 ? n : null;
}

/** The config Publish sends. `enabled` comes from the caller (the live state). Null when the audience is not valid. */
export function draftToConfig(draft: IntroDraft, enabled: boolean): IntroVideoConfig | null {
  const audience = parseAudience(draft.audiencePct);
  if (audience === null) return null;
  const video = (loc: ExperienceLocale): VideoAsset | null => {
    const m = draft.videos[loc];
    if (!m) return null;
    const text = draft.descriptions[loc].trim();
    return { ...m, description: text === '' ? null : text };
  };
  return { schema: 1, enabled, audience_pct: audience, videos: { en: video('en'), es: video('es') } };
}

/** JSON with sorted keys, so key order never reads as a change. */
function stable(x: unknown): string {
  if (Array.isArray(x)) return `[${x.map(stable).join(',')}]`;
  if (x && typeof x === 'object') {
    const o = x as Record<string, unknown>;
    return `{${Object.keys(o)
      .sort()
      .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`)
      .join(',')}}`;
  }
  return JSON.stringify(x) ?? 'null';
}

export function sameConfig(a: IntroVideoConfig | null, b: IntroVideoConfig | null): boolean {
  return stable(a) === stable(b);
}

// ─── Publish ────────────────────────────────────────────────────────────────

export interface IntroPublishState {
  /** The draft differs from what is live. */
  dirty: boolean;
  canPublish: boolean;
  /** Block Publish. */
  errors: string[];
  /** Shown, never block. */
  warnings: string[];
  /** Why Publish is disabled, in one sentence; null when it is enabled. */
  blockedReason: string | null;
  /** What Publish sends; null when the draft cannot become a config. */
  config: IntroVideoConfig | null;
}

export function introPublishState(args: {
  live: IntroVideoConfig | null;
  draft: IntroDraft;
  canWrite: boolean;
}): IntroPublishState {
  const { live, draft, canWrite } = args;
  const enabled = live?.enabled ?? false;
  const config = draftToConfig(draft, enabled);
  const errors: string[] = [];
  const warnings: string[] = [];

  if (config === null) errors.push('Enter a whole number from 0 to 100 for the share of new installs.');
  for (const loc of LOCALES) {
    if (draft.descriptions[loc].trim().length > DESCRIPTION_MAX_CHARS) {
      errors.push(`The ${LANGUAGE_NAME[loc]} description is too long. The limit is ${DESCRIPTION_MAX_CHARS} characters.`);
    }
  }
  if (enabled && !draft.videos.en) errors.push('Add an English video. The intro is on, so it needs one.');

  for (const loc of LOCALES) {
    if (draft.videos[loc] && draft.descriptions[loc].trim() === '') {
      warnings.push(
        `The ${LANGUAGE_NAME[loc]} description is empty. People who use a screen reader will not know what the video shows.`,
      );
    }
  }

  // Compare the normalised configs: trimming a description is not a change.
  const baseline = live ?? draftToConfig(draftFrom(null), false);
  const dirty = config === null || !sameConfig(config, baseline);
  const canPublish = canWrite && dirty && errors.length === 0;
  let blockedReason: string | null = null;
  if (!canWrite) blockedReason = READ_ONLY;
  else if (errors.length > 0) blockedReason = 'Correct the problems above before you publish.';
  else if (!dirty) blockedReason = 'Nothing changed. Upload a video or change a field to publish.';
  return { dirty, canPublish, errors, warnings, blockedReason, config };
}

// ─── the on/off switch ──────────────────────────────────────────────────────

export interface SwitchState {
  on: boolean;
  canToggle: boolean;
  reason: string | null;
  /** The config the switch publishes: the live one with `enabled` flipped. */
  next: IntroVideoConfig | null;
}

export function switchState(args: { live: IntroVideoConfig | null; canWrite: boolean }): SwitchState {
  const { live, canWrite } = args;
  const on = live?.enabled ?? false;
  if (!live || (!on && !live.videos.en)) {
    return {
      on,
      canToggle: false,
      reason: canWrite ? 'Publish an English video first. Then you can turn the intro on.' : READ_ONLY,
      next: null,
    };
  }
  const next = { ...live, enabled: !on };
  if (!canWrite) return { on, canToggle: false, reason: READ_ONLY, next };
  return { on, canToggle: true, reason: null, next };
}

// ─── Restore ────────────────────────────────────────────────────────────────

export interface RestorePlan {
  config: IntroVideoConfig;
  based_on_version: number;
  /** What else the restore does, said before the admin confirms. */
  effects: string[];
}

export function restorePlan(args: {
  config: IntroVideoConfig;
  latestVersion: number;
  live: IntroVideoConfig | null;
  dirty: boolean;
}): RestorePlan {
  const { config, latestVersion, live, dirty } = args;
  const effects: string[] = [];
  const wasOn = live?.enabled ?? false;
  if (config.enabled && !wasOn) effects.push('This turns the intro on.');
  if (!config.enabled && wasOn) effects.push('This turns the intro off.');
  if (dirty) effects.push('Your changes that are not published will be lost.');
  return { config, based_on_version: latestVersion, effects };
}

// ─── History ────────────────────────────────────────────────────────────────

/** One line for the history: "English 0:15, 4:5 · Spanish: none · 100 % of new installs". */
export function introHistorySummary(c: IntroVideoConfig): string {
  const en = c.videos.en ? `English ${formatDuration(c.videos.en.duration_ms)}, ${shapeLabel(c.videos.en.width, c.videos.en.height)}` : 'English: none';
  const es = c.videos.es ? `Spanish ${formatDuration(c.videos.es.duration_ms)}, ${shapeLabel(c.videos.es.width, c.videos.es.height)}` : 'Spanish: none';
  return `${en} · ${es} · ${c.audience_pct} % of new installs`;
}

/** The poster for the history thumbnail. */
export function introPosterOf(c: IntroVideoConfig): string | null {
  return c.videos.en?.poster_url ?? null;
}
